import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search, MapPin, Navigation, QrCode, ChevronRight, Utensils, X, Loader2,
  Menu as MenuIcon, Star, Map, List, Phone, Clock, ExternalLink, Percent, Flame
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  'Halal': '&#9790;', 'Burger': '\u{1F354}', 'Pizza': '\u{1F355}', 'Asiatique': '\u{1F962}',
  'Indien': '\u{1F35B}', 'Mexicain': '\u{1F32E}', 'Fran\u00e7ais': '\u{1F956}', 'Italien': '\u{1F35D}',
  'Japonais': '\u{1F363}', 'Cr\u00e9ole': '\u{1F372}', 'Fast-food': '\u{1F35F}', 'Fruits de mer': '\u{1F990}',
  'V\u00e9g\u00e9tarien': '\u{1F957}', 'Grillades': '\u{1F969}', 'P\u00e2tisserie': '\u{1F9C1}', 'Caf\u00e9': '\u2615'
};

// La Reunion center
const DEFAULT_CENTER = [-21.115, 55.536];
const DEFAULT_ZOOM = 11;

// Lazy load map component
const MapView = React.lazy(() => import('../components/ExplorerMap'));

const Explorer = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locating, setLocating] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchRestaurants();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/categories`);
      const data = await res.json();
      setCategories(data);
    } catch (err) { console.error(err); }
  };

  const fetchRestaurants = async (params = {}) => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (params.search) query.append('search', params.search);
      if (params.category) query.append('category', params.category);
      const res = await fetch(`${API_URL}/api/public/restaurants?${query.toString()}`);
      const data = await res.json();
      setRestaurants(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRestaurants({ search, category: selectedCategory });
  };

  const handleCategoryClick = (cat) => {
    if (selectedCategory === cat) {
      setSelectedCategory(null);
      fetchRestaurants({ search });
    } else {
      setSelectedCategory(cat);
      fetchRestaurants({ search, category: cat });
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory(null);
    fetchRestaurants({});
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation([pos.coords.latitude, pos.coords.longitude]);
        setLocating(false);
        if (viewMode !== 'map') setViewMode('map');
      },
      () => { setLocating(false); },
      { timeout: 10000 }
    );
  };

  const isOpen = (hours) => {
    if (!hours || Object.keys(hours).length === 0) return null;
    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const now = new Date();
    const today = days[now.getDay()];
    const todayHours = hours[today];
    if (!todayHours || todayHours === 'Ferm\u00e9') return false;
    return true;
  };

  const getCatIcon = (cat) => CATEGORY_ICONS[cat] || '\u{1F37D}\uFE0F';

  const restaurantsWithCoords = restaurants.filter(r => r.latitude && r.longitude);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="explorer-page">
      {/* Header */}
      <header className="sticky top-0 z-[1000] bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
                <QrCode className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-gray-900 tracking-tight">ZOTMENU</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-sm font-medium text-gray-600 hover:text-gray-900">Connexion</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gray-900 hover:bg-gray-800 text-white h-9 px-4 rounded-full text-sm font-medium">Inscription</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Search bar */}
      <section className="bg-gradient-to-b from-gray-900 to-gray-800 pt-6 pb-4 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight text-center">
            Découvrez les restaurants
          </h1>
          <form onSubmit={handleSearch} className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input type="text" placeholder="Rechercher un restaurant, une cuisine..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="h-12 pl-12 pr-28 rounded-full bg-white border-0 text-base shadow-xl" data-testid="explorer-search" />
            <Button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-medium" data-testid="explorer-search-btn">
              Rechercher
            </Button>
          </form>
          <div className="flex items-center justify-center gap-3 mt-3">
            <Button variant="outline" size="sm" onClick={handleLocateMe} disabled={locating}
              className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 text-sm" data-testid="explorer-locate-btn">
              {locating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Navigation className="h-4 w-4 mr-1.5" />}
              Autour de moi
            </Button>
            {/* Toggle Carte / Liste */}
            <div className="flex bg-white/10 rounded-full p-0.5" data-testid="view-toggle">
              <button onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-gray-900 shadow' : 'text-white/70 hover:text-white'}`}
                data-testid="view-toggle-list">
                <List className="h-4 w-4" /> Liste
              </button>
              <button onClick={() => setViewMode('map')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${viewMode === 'map' ? 'bg-white text-gray-900 shadow' : 'text-white/70 hover:text-white'}`}
                data-testid="view-toggle-map">
                <Map className="h-4 w-4" /> Carte
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Category icons */}
      <section className="bg-white border-b border-gray-100 py-3 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {categories.map((cat) => (
              <button key={cat} onClick={() => handleCategoryClick(cat)}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-2xl min-w-[68px] transition-all ${
                  selectedCategory === cat ? 'bg-gray-900 text-white shadow-lg scale-105' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`} data-testid={`cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="text-xl leading-none" dangerouslySetInnerHTML={{ __html: getCatIcon(cat) }} />
                <span className="text-[11px] font-medium whitespace-nowrap">{cat}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active filters bar */}
      {(search || selectedCategory) && (
        <div className="bg-white border-b border-gray-100 py-2 px-4">
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <p className="text-sm text-gray-500">{restaurants.length} résultat{restaurants.length > 1 ? 's' : ''}</p>
            {selectedCategory && (
              <Badge className="bg-gray-900 text-white text-xs flex items-center gap-1">
                <span dangerouslySetInnerHTML={{ __html: getCatIcon(selectedCategory) }} />
                {selectedCategory}
                <button onClick={() => handleCategoryClick(selectedCategory)} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            <button onClick={clearFilters} className="text-sm text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1 ml-auto">
              <X className="h-3.5 w-3.5" /> Réinitialiser
            </button>
          </div>
        </div>
      )}

      {/* Content: List or Map */}
      <div className="flex-1">
        {viewMode === 'list' ? (
          <section className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {!search && !selectedCategory && (
                <p className="text-sm text-gray-500 mb-4">{loading ? 'Chargement...' : `${restaurants.length} restaurant${restaurants.length > 1 ? 's' : ''}`}</p>
              )}
              {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
              ) : restaurants.length === 0 ? (
                <div className="text-center py-20">
                  <Utensils className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Aucun restaurant trouvé</h3>
                  <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {restaurants.map((r) => <RestaurantCard key={r.id} r={r} isOpen={isOpen} getCatIcon={getCatIcon} />)}
                </div>
              )}
            </div>
          </section>
        ) : (
          <div className="relative" style={{ height: 'calc(100vh - 220px)' }} data-testid="map-container">
            {loading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
            ) : (
              <React.Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>}>
                <MapView
                  restaurants={restaurantsWithCoords}
                  allRestaurants={restaurants}
                  center={userLocation || DEFAULT_CENTER}
                  zoom={userLocation ? 14 : DEFAULT_ZOOM}
                  userLocation={userLocation}
                  selectedRestaurant={selectedRestaurant}
                  onSelectRestaurant={setSelectedRestaurant}
                  isOpen={isOpen}
                  getCatIcon={getCatIcon}
                />
              </React.Suspense>
            )}
            {/* Floating restaurant card on map */}
            {selectedRestaurant && viewMode === 'map' && (
              <div className="absolute bottom-4 left-4 right-4 z-[900] max-w-md mx-auto animate-in slide-in-from-bottom-4" data-testid="map-restaurant-card">
                <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden">
                  <CardContent className="p-0">
                    <button onClick={() => setSelectedRestaurant(null)} className="absolute top-2 right-2 z-10 bg-white/80 backdrop-blur rounded-full p-1.5 hover:bg-white shadow">
                      <X className="h-4 w-4 text-gray-600" />
                    </button>
                    <div className="flex gap-0">
                      <div className="w-28 h-28 flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                        {selectedRestaurant.logo_url ? (
                          <img src={selectedRestaurant.logo_url} alt={selectedRestaurant.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Utensils className="h-8 w-8" style={{ color: selectedRestaurant.primary_color }} />
                          </div>
                        )}
                      </div>
                      <div className="p-3 flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-gray-900 truncate text-sm">{selectedRestaurant.name}</h3>
                        {selectedRestaurant.address && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate">
                            <MapPin className="h-3 w-3 flex-shrink-0" />{selectedRestaurant.address}
                          </p>
                        )}
                        {selectedRestaurant.phone && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Phone className="h-3 w-3 flex-shrink-0" />{selectedRestaurant.phone}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-0.5">
                          {selectedRestaurant.restaurant_categories?.slice(0, 2).map(c => (
                            <span key={c} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{c}</span>
                          ))}
                          {isOpen(selectedRestaurant.opening_hours) !== null && (
                            <span className={`text-[10px] font-medium ${isOpen(selectedRestaurant.opening_hours) ? 'text-emerald-600' : 'text-gray-400'}`}>
                              {isOpen(selectedRestaurant.opening_hours) ? 'Ouvert' : 'Fermé'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center flex-wrap gap-1.5 mt-1">
                          {selectedRestaurant.has_active_promo && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full border border-red-200" data-testid="map-card-promo-badge">
                              <Percent className="h-2.5 w-2.5" /> {selectedRestaurant.promo_name || 'Promo'}
                            </span>
                          )}
                          {selectedRestaurant.featured_count > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full border border-amber-200" data-testid="map-card-popular-badge">
                              <Flame className="h-2.5 w-2.5" /> {selectedRestaurant.featured_count} populaire{selectedRestaurant.featured_count > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <Link to={`/m/${selectedRestaurant.slug}`}
                          className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-orange-500 hover:text-orange-600" data-testid="map-card-menu-link">
                          Voir le menu <ExternalLink className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Restaurant card component for list view
const RestaurantCard = ({ r, isOpen, getCatIcon }) => {
  const openStatus = isOpen(r.opening_hours);
  const cats = r.restaurant_categories || [];
  return (
    <Link to={`/m/${r.slug}`} className="group" data-testid={`restaurant-card-${r.slug}`}>
      <Card className={`overflow-hidden rounded-2xl hover:shadow-lg transition-all duration-300 h-full ${
        r.is_boosted ? 'border-2 border-orange-300 shadow-md shadow-orange-100' : 'border border-gray-100 hover:border-orange-200'
      }`}>
        <CardContent className="p-0">
          <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
            {r.logo_url ? (
              <img src={r.logo_url} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: r.primary_color + '20' }}>
                  <Utensils className="h-8 w-8" style={{ color: r.primary_color }} />
                </div>
              </div>
            )}
            <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
              {cats.includes('Halal') && <Badge className="bg-emerald-500 text-white text-[10px] font-semibold shadow-lg">Halal</Badge>}
              {cats.filter(c => c !== 'Halal').slice(0, 2).map(c => (
                <Badge key={c} variant="secondary" className="bg-white/90 backdrop-blur text-gray-700 text-[10px] font-medium shadow">{c}</Badge>
              ))}
            </div>
            {openStatus !== null && (
              <div className="absolute top-3 right-3">
                <Badge className={`text-[10px] font-medium shadow ${openStatus ? 'bg-emerald-500 text-white' : 'bg-gray-500 text-white'}`}>
                  {openStatus ? 'Ouvert' : 'Fermé'}
                </Badge>
              </div>
            )}
            {r.is_boosted && (
              <div className="absolute bottom-3 left-3">
                <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] font-semibold shadow-lg flex items-center gap-1">
                  <Star className="h-3 w-3" /> En vedette
                </Badge>
              </div>
            )}
            {r.has_active_promo && (
              <div className={`absolute bottom-3 ${r.is_boosted ? 'left-28' : 'left-3'}`}>
                <Badge className="bg-red-500 text-white text-[10px] font-semibold shadow-lg flex items-center gap-1" data-testid={`promo-badge-${r.slug}`}>
                  <Percent className="h-3 w-3" /> Promo
                </Badge>
              </div>
            )}
            {r.featured_count > 0 && (
              <div className="absolute bottom-3 right-3">
                <Badge className="bg-amber-500 text-white text-[10px] font-semibold shadow-lg flex items-center gap-1" data-testid={`popular-badge-${r.slug}`}>
                  <Flame className="h-3 w-3" /> Populaire
                </Badge>
              </div>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-gray-900 group-hover:text-orange-500 transition-colors truncate">{r.name}</h3>
                {r.address && (
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" /><span className="truncate">{r.address}</span>
                  </p>
                )}
              </div>
              <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-orange-500 transition-colors flex-shrink-0 mt-0.5" />
            </div>
            {r.description && <p className="text-sm text-gray-500 mt-2 line-clamp-2">{r.description}</p>}
            <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><MenuIcon className="h-3.5 w-3.5" />{r.dish_count} plat{r.dish_count > 1 ? 's' : ''}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default Explorer;
